import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Activity, Settings2, ScrollText, KeyRound, QrCode } from "lucide-react";
import { FrequentationChart } from "@/components/FrequentationChart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BastionPanel = ({ user }) => {
  const adminId = user.id;
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState({ enabled: false, must_change_password: false });
  const [qr, setQr] = useState(null);
  const [code, setCode] = useState("");
  const [nouveauMdp, setNouveauMdp] = useState("");

  const charger = useCallback(() => {
    const p = { params: { admin_id: adminId } };
    axios.get(`${API}/admin/bastion-stats`, p).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/admin/logs`, p).then((r) => setLogs(r.data)).catch(() => {});
    axios.get(`${API}/admin/2fa/status`, p).then((r) => setStatus(r.data)).catch(() => {});
  }, [adminId]);

  useEffect(() => { charger(); }, [charger]);

  const majParam = async (patch) => {
    try {
      const { data } = await axios.put(`${API}/admin/bastion-settings`, { admin_id: adminId, ...patch });
      setStats((s) => ({ ...s, ...data }));
      toast.success("Paramètres mis à jour.");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const lancer2fa = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/2fa/setup`, { admin_id: adminId });
      setQr(data.qr_data_url);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const confirmer2fa = async () => {
    try {
      await axios.post(`${API}/admin/2fa/confirm`, { admin_id: adminId, code });
      toast.success("2FA activée. Un code sera demandé à chaque connexion.");
      setQr(null); setCode(""); charger();
    } catch (e) { toast.error(e.response?.data?.detail || "Code invalide"); }
  };

  const changerMdp = async () => {
    try {
      await axios.post(`${API}/admin/changer-mot-de-passe`, { admin_id: adminId, nouveau: nouveauMdp });
      toast.success("Mot de passe changé.");
      setNouveauMdp(""); charger();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const carte = (label, valeur) => (
    <div className="bg-white border border-[#E5DFD5] rounded-2xl p-4">
      <div className="font-heading text-3xl font-extrabold text-stone-900">{valeur ?? "—"}</div>
      <div className="text-xs font-medium text-stone-500 mt-1">{label}</div>
    </div>
  );

  return (
    <section className="mt-14" data-testid="bastion-panel">
      <h2 className="font-heading text-2xl font-extrabold text-stone-900 flex items-center gap-2.5">
        <ShieldCheck size={24} className="text-[#0A5C36]" /> Bastion — Tableau de bord
      </h2>

      {status.must_change_password && (
        <div className="mt-4 bg-amber-50 border border-amber-300 rounded-xl p-4" data-testid="bastion-change-pwd">
          <p className="text-sm font-semibold text-amber-800">⚠️ Changez le mot de passe par défaut dès maintenant.</p>
          <div className="flex gap-2 mt-3">
            <input type="password" value={nouveauMdp} onChange={(e) => setNouveauMdp(e.target.value)}
              placeholder="Nouveau mot de passe (10+ caractères)"
              className="flex-1 rounded-xl border border-amber-300 px-3 py-2 text-sm" data-testid="bastion-new-pwd" />
            <button onClick={changerMdp} data-testid="bastion-save-pwd"
              className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-4 rounded-full">
              <KeyRound size={14} /> Changer
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="bastion-stats">
        {carte("Comptes mock", stats?.mock_accounts)}
        {carte("Annonces mock", stats?.mock_annonces)}
        {carte("Créés aujourd'hui", stats?.created_today)}
        {carte("Actions journalisées", stats?.logs_count)}
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <FrequentationChart />

        {/* Paramètres */}
        <div className="bg-white border border-[#E5DFD5] rounded-2xl p-5">
          <h3 className="font-semibold text-stone-900 flex items-center gap-2"><Settings2 size={18} /> Paramètres</h3>
          <label className="flex items-center justify-between mt-4 text-sm">
            <span>Mode mock activé</span>
            <input type="checkbox" data-testid="bastion-mock-toggle" className="w-5 h-5 accent-[#0A5C36]"
              checked={!!stats?.mock_mode_enabled}
              onChange={(e) => majParam({ mock_mode_enabled: e.target.checked })} />
          </label>
          <label className="flex items-center justify-between mt-4 text-sm">
            <span>Limite / jour</span>
            <input type="number" min="1" data-testid="bastion-daily-limit"
              className="w-24 rounded-lg border border-[#E5DFD5] px-2 py-1 text-right"
              defaultValue={stats?.mock_daily_limit}
              onBlur={(e) => majParam({ mock_daily_limit: Number(e.target.value) })} />
          </label>
        </div>

        {/* 2FA */}
        <div className="bg-white border border-[#E5DFD5] rounded-2xl p-5">
          <h3 className="font-semibold text-stone-900 flex items-center gap-2"><QrCode size={18} /> Double authentification</h3>
          <p className="text-sm mt-2">
            Statut : <b className={status.enabled ? "text-[#0A5C36]" : "text-stone-500"}>{status.enabled ? "Activée ✅" : "Désactivée"}</b>
          </p>
          {!status.enabled && !qr && (
            <button onClick={lancer2fa} data-testid="bastion-2fa-start"
              className="mt-3 inline-flex items-center gap-1.5 bg-[#0A5C36] hover:bg-[#084027] text-white text-sm font-semibold px-4 py-2 rounded-full">
              <QrCode size={14} /> Activer via Google Authenticator
            </button>
          )}
          {qr && (
            <div className="mt-3 space-y-2">
              <img src={qr} alt="QR 2FA" className="w-40 h-40" data-testid="bastion-2fa-qr" />
              <p className="text-xs text-stone-500">Scannez avec Google Authenticator, puis entrez le code :</p>
              <div className="flex gap-2">
                <input inputMode="numeric" maxLength={6} value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="flex-1 rounded-xl border border-[#E5DFD5] px-3 py-2 text-sm text-center font-mono tracking-[0.3em]"
                  placeholder="123456" data-testid="bastion-2fa-code" />
                <button onClick={confirmer2fa} disabled={code.length !== 6} data-testid="bastion-2fa-confirm"
                  className="bg-[#0A5C36] disabled:opacity-50 text-white text-sm font-semibold px-4 rounded-full">Confirmer</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Journal d'audit */}
      <div className="mt-6">
        <h3 className="font-semibold text-stone-900 flex items-center gap-2"><ScrollText size={18} /> Journal d'audit</h3>
        <div className="mt-3 space-y-1.5" data-testid="bastion-logs">
          {logs.length === 0 ? (
            <p className="text-sm text-stone-400">Aucune action journalisée.</p>
          ) : logs.slice(0, 30).map((l) => (
            <div key={l.id} className="bg-white border border-[#E5DFD5] rounded-lg px-3 py-2 text-xs text-stone-600 flex flex-wrap gap-x-2">
              <span className="text-stone-400">{new Date(l.created_at).toLocaleString("fr-FR")}</span>
              <b className="text-stone-900">{l.action}</b>
              <span className="text-stone-400">· {l.admin_email} · IP {l.ip}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
